/**
 * Owner: Masa Bayu
 * File: MarketplaceTopBar.tsx
 * Date:Time: 2026-10-19 13:40
 * Description: §373 Fasa 1 + §407 — topbar semua halaman mengikuti konsep
 *   header home: logo + tagline biru Figtree, pil carian berikon + pemilih
 *   kategori, tindakan bulat (wishlist · troli · profil · Menu). Nav emas
 *   pindah kekal ke panel Menu.
 */

import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { QX_TAGLINE } from '../brand/brand-assets';
import { useOptionalPwaPhaseA } from '../pwa/PwaPhaseAContext';
import { LandingIcon } from '../home/marketplace/landing/LandingIcons';
import { MARKETPLACE_CATEGORY_ORDER } from '../home/marketplace/marketplace-projection';
import { getMarketplaceLocaleProfile } from '../home/marketplace/marketplace-locale';
import { accountEntryPath } from '../auth/session-auth';
import { qxCategoryName } from '@qx/qxread-core';

export interface MarketplaceTopBarProps {
  readonly onNavigate: (path: string) => void;
  readonly onSearchSubmit?: (query: string) => void;
  readonly currentPath?: string;
  readonly signedIn?: boolean;
}

const NAV_LINKS: readonly { readonly label: string; readonly path: string }[] = [
  { label: 'Home', path: '/' },
  { label: 'Browse', path: '/browse' },
  { label: 'Categories', path: '/categories' },
  { label: 'Authors', path: '/authors' },
  { label: 'New Releases', path: '/collections' },
  { label: 'Deals', path: '/topics' },
  { label: 'My Library', path: '/library' },
];

const MENU_LINKS: readonly { readonly label: string; readonly path: string }[] = [
  { label: 'Discover', path: '/discover' },
  { label: 'Author', path: '/author/dashboard' },
  { label: 'Admin', path: '/admin' },
  { label: 'Account', path: '/account' },
  { label: 'PWA', path: '/offline' },
];

export function MarketplaceTopBar({
  onNavigate,
  onSearchSubmit,
  currentPath,
  signedIn = false,
}: MarketplaceTopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const pwa = useOptionalPwaPhaseA();
  const language = getMarketplaceLocaleProfile().language;

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [menuOpen]);

  const isActive = (path: string): boolean => {
    if (typeof currentPath !== 'string') {
      return false;
    }
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath === path || currentPath.startsWith(`${path}/`);
  };

  const navigateFromMenu = (path: string) => {
    setMenuOpen(false);
    onNavigate(path);
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = query.trim();
    if (onSearchSubmit !== undefined && q.length > 0) {
      onSearchSubmit(q);
    } else {
      onNavigate(q.length > 0 ? `/search?q=${encodeURIComponent(q)}` : '/search');
    }
  };

  return (
    <div className="marketplace-topbar">
      <button
        type="button"
        className="marketplace-topbar__mark"
        onClick={() => {
          onNavigate('/');
        }}
        aria-label="QX Ebook home"
      >
        <img
          className="marketplace-topbar__mark-img"
          src="/brand/qx.png"
          alt=""
          width={43}
          height={43}
        />
        <span className="marketplace-topbar__mark-text">
          <strong>{QX_TAGLINE.top}</strong>
          <small>{QX_TAGLINE.bottom}</small>
        </span>
      </button>

      <form className="marketplace-topbar__search" onSubmit={submitSearch} role="search">
        <LandingIcon name="search" size={16} className="marketplace-topbar__search-icon" />
        <input
          name="q"
          type="search"
          value={query}
          placeholder="Search books, authors, genres, topics..."
          aria-label="Search books, authors, topics"
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <select
          className="marketplace-topbar__search-cat"
          aria-label="Browse books by category"
          value="all"
          onChange={(event) => {
            const value = event.target.value;
            onNavigate(value === 'all' ? '/all-books' : `/all-books?category=${value}`);
          }}
        >
          <option value="all">All Books</option>
          {MARKETPLACE_CATEGORY_ORDER.map((id) => (
            <option key={id} value={id}>
              {qxCategoryName(id, language)}
            </option>
          ))}
        </select>
        <button type="submit" className="visually-hidden" tabIndex={-1} aria-hidden="true">
          Search
        </button>
      </form>

      <div className="marketplace-topbar__actions">
        <button
          type="button"
          className="marketplace-topbar__icon-btn"
          aria-label="My wishlist"
          onClick={() => {
            onNavigate('/library');
          }}
        >
          <LandingIcon name="heart" size={18} />
        </button>
        <button
          type="button"
          className="marketplace-topbar__icon-btn"
          onClick={() => {
            onNavigate('/cart');
          }}
          aria-label="Cart"
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 5h2l2.2 11h9.6L20 8H7" />
            <circle cx="9.5" cy="19" r="1.4" />
            <circle cx="16.5" cy="19" r="1.4" />
          </svg>
        </button>
        <button
          type="button"
          className="marketplace-topbar__icon-btn marketplace-topbar__profile-btn"
          onClick={() => {
            // §414 — aturan sesi sebenar: tetamu ke login, ahli ke profil reader.
            onNavigate(accountEntryPath(signedIn));
          }}
          aria-label={signedIn ? 'Profile' : 'Sign in'}
        >
          <svg
            viewBox="0 0 24 24"
            width={20}
            height={20}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="8.5" r="3.5" />
            <path d="M5 19c1.2-3 4-4.5 7-4.5s5.8 1.5 7 4.5" />
          </svg>
        </button>
        <div className="marketplace-topbar__menu" ref={menuRef}>
          <button
            type="button"
            className="marketplace-topbar__icon-btn"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="marketplace-topbar-menu"
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width={20}
              height={20}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          {menuOpen ? (
            <nav
              className="marketplace-topbar__menu-panel"
              id="marketplace-topbar-menu"
              aria-label="More navigation"
            >
              <div className="marketplace-topbar__menu-gold">
                {NAV_LINKS.map((link) => (
                  <button
                    key={link.path}
                    type="button"
                    className="marketplace-topbar__menu-link"
                    data-active={isActive(link.path)}
                    {...(isActive(link.path) ? { 'aria-current': 'page' as const } : {})}
                    onClick={() => {
                      navigateFromMenu(link.path);
                    }}
                  >
                    {link.label}
                  </button>
                ))}
              </div>
              {pwa !== null && pwa.install.mode === 'promptable' ? (
                <button
                  type="button"
                  className="marketplace-topbar__menu-link marketplace-topbar__menu-install"
                  onClick={() => {
                    setMenuOpen(false);
                    void pwa.promptInstall();
                  }}
                >
                  Install App
                </button>
              ) : null}
              {pwa !== null && pwa.install.mode === 'installing' ? (
                <p className="marketplace-topbar__menu-note" role="status">
                  <span className="qx-spinner" aria-hidden="true" /> Installing app…
                </p>
              ) : null}
              {pwa !== null && pwa.install.mode === 'a2hs_guided' ? (
                <p className="marketplace-topbar__menu-note">
                  iPhone/iPad: Share → Add to Home Screen
                </p>
              ) : null}
              {pwa !== null &&
              (pwa.install.mode === 'standalone' || pwa.install.mode === 'installed') ? (
                <p className="marketplace-topbar__menu-note">
                  Installed — open from your home screen
                </p>
              ) : null}
              {MENU_LINKS.map((link) => (
                <button
                  key={link.path}
                  type="button"
                  className="marketplace-topbar__menu-link"
                  data-active={isActive(link.path)}
                  {...(isActive(link.path) ? { 'aria-current': 'page' as const } : {})}
                  onClick={() => {
                    navigateFromMenu(link.path);
                  }}
                >
                  {link.label}
                </button>
              ))}
            </nav>
          ) : null}
        </div>
      </div>
    </div>
  );
}
